import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toJS } from 'mobx';

import { fetchPickupPoint } from '../../../../utils/pickupPointApi';

export default function usePickupPoints(addresses, enabled = true) {
  const { i18n } = useTranslation();
  const [points, setPoints] = useState({});
  const [loading, setLoading] = useState(false);

  // a string, so a new array identity alone does not refetch
  const lockerIds = (addresses || [])
    .map((a) => a.pudo && a.pudo.lockerId)
    .filter(Boolean)
    .join(',');

  useEffect(() => {
    if (!enabled || !lockerIds) {
      return;
    }
    const missing = lockerIds.split(',').filter((id) => !points[id]);
    if (missing.length === 0) {
      return;
    }
    setLoading(true);
    Promise.all(
      missing.map((id) =>
        fetchPickupPoint(id, i18n.language)
          .then((point) => ({ id, point }))
          .catch((err) => {
            console.log('pickup point lookup err', id, err);
            return null;
          }),
      ),
    ).then((results) => {
      setPoints((prev) => {
        const next = { ...prev };
        results.filter(Boolean).forEach(({ id, point }) => {
          next[id] = point;
        });
        return next;
      });
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockerIds, enabled]);

  const decorated = useMemo(
    () =>
      (addresses || []).map((a) => {
        const point = a.pudo && points[a.pudo.lockerId];
        return point ? { ...toJS(a), ...point } : a;
      }),
    [addresses, points],
  );

  return { addresses: decorated, loading };
}

// A pickup point ships to the locker, so the order's formatted string (the
// customer's own street) is not what to show: the looked-up point is.
export const usePickupAddress = (address, isPickup) => {
  const { addresses } = usePickupPoints(isPickup ? [address] : [], isPickup);
  const point = addresses[0];
  const formatted = [point?.addressLine1, point?.city, point?.postalCode]
    .filter(Boolean)
    .join(', ');
  return {
    value: (isPickup && formatted) || address?.formatted,
    // the shop name, since the order payload sends the label empty
    label: address?.label || point?.label,
  };
};

// For lists of shipments, where the hook can't be called per item.
// Render it only for pickup point addresses.
export function PickupAwareAddress({ address }) {
  return usePickupAddress(address, true).value || null;
}
